/**
 * "Claim Gift" button for unlocked tier gifts with a single default variant.
 *
 * cart-tier-gift-placeholder.liquid renders this button only when the tier's
 * Auto Add Free Gift switch is off AND the gift has nothing to pick — one click
 * puts the tier's configured Free Gift variant in the cart, and that is the
 * shopper's last decision, because a single-variant gift line offers no control
 * afterwards. A gift with colour or size choices never reaches here: its
 * placeholder opens the picker instead, and the line is created by
 * cart-gift-swap.js once the shopper chooses.
 *
 * Its own module rather than a branch in cart.js: that file's drawer click
 * delegate returns early on `.cart-item--tier-gift` (cart.js:349), which is the
 * class this row carries, so a handler added there would never fire. Registered
 * at document level rather than on the button, following cart-gift-swap.js —
 * component-cart-drawer.js replaces the drawer's innerHTML on every cart:change
 * and would drop any listener bound to the element.
 */

const SOURCE = 'cart-tier-claim';

/* The button stays on screen until the drawer re-renders, so a second click
   would add a second gift line for the reconciler to clean up — spending two of
   its twelve ops. `disabled` covers the same ground; this also covers a click
   landing on a different claim button while the first is still in flight. */
let claiming = false;

function setBusy(button, busy) {
  button.disabled = busy;
  button.setAttribute('aria-busy', busy ? 'true' : 'false');

  if (busy) {
    if (!button.dataset.claimingLabel) return;
    button.dataset.idleLabel = button.textContent.trim();
    button.textContent = button.dataset.claimingLabel;
  } else if (button.dataset.idleLabel) {
    button.textContent = button.dataset.idleLabel;
  }
}

/* Same slot the line-item rows use, so a failed claim reads like any other cart
   error. Empty is styled away by .cart-item__error:empty. */
function showError(button, message) {
  const slot = button.closest('.cart-item')?.querySelector('.cart-item__error');
  if (slot) slot.textContent = message;
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-gift-claim]');
  if (!button || button.disabled) return;

  event.preventDefault();
  /* cart.js already bails on this row, but theme.js keeps legacy document-level
     cart delegates of its own — stop here rather than depend on that. */
  event.stopPropagation();

  const variantId = button.dataset.variantId;
  const tierId = button.dataset.tierId;
  if (!variantId || !tierId || claiming || !window.Cart) return;

  claiming = true;
  setBusy(button, true);
  showError(button, '');

  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', '1');
  body.append('properties[_cart_tier_gift]', 'true');
  body.append('properties[_cart_tier_id]', String(tierId));

  try {
    /* Default withSections: the drawer has to re-render, because that is what
       replaces this placeholder row with the real gift line. */
    await window.Cart.add(body, SOURCE);
    /* No setBusy(false) on success — the row this button lives in is gone. */
  } catch (error) {
    setBusy(button, false);
    showError(button, error?.description || error?.message || button.dataset.errorLabel || '');
  } finally {
    claiming = false;
  }
});
